import {with_} from "@web-libs/util/with/with";
import {isSameDay, sortByDateAsc} from "@web-libs/util/dates/date";
import {v4 as uuidv4} from "uuid";
import {ChatMessage} from "@web-libs/features/chat/domain/types/chat-message.types";
import {ChatDayGroup, ChatMessageGroup} from "@web-libs/features/chat/domain/types/message-group.types";

type ChatDayUngrouped = {
    id: string;
    date: Date;
    messages: ChatMessage[];
}

/**
 *  PUBLIC FUNCTIONS
 */

export const sortMessages = (messages: ChatMessage[]): ChatMessage[] =>
    sortByDateAsc(messages, message => message.sendDate);

export const groupMessages = (messages:ChatMessage[], previousMessageGroups: ChatMessageGroup[]): ChatMessageGroup[] =>
    _mapGroupsToPreviousIds(_groupMessages(messages), previousMessageGroups);

export const groupMessagesByDay = (messages: ChatMessage[], previousDayGroups: ChatDayGroup[]): ChatDayGroup[] => {
    const ungroupedDays: ChatDayUngrouped[] = messages.reduce((days: ChatDayUngrouped[], message: ChatMessage) => {
        const lastDay = days[days.length - 1];

        if (lastDay && isSameDay(lastDay.date, message.sendDate)) {
            lastDay.messages.push(message);
        } else {
            const newDate = new Date(message.sendDate.toDateString());
            const newDay: ChatDayUngrouped = {
                id: newDate.toDateString(),
                date: newDate,
                messages: [message]
            };

            days.push(newDay);
        }
        return days;
    },[]);
    return ungroupedDays.map(day => {
        const previousDay = previousDayGroups.find(previousDay => previousDay.id === day.id);
        return {
            id: day.id,
            date: day.date,
            messages: groupMessages(day.messages, previousDay?.messages || [])
        }
    });
}

/**
 * PRIVATE FUNCTIONS
 */
const _findPreviousGroup = (previousGroups: ChatMessageGroup[], group: ChatMessageGroup) => previousGroups.find(
    previousGroup => _groupIsPreviousGroup(group, previousGroup)
);

const _groupIsPreviousGroup = (group:ChatMessageGroup, previousGroup:ChatMessageGroup):boolean => {
    const previousMessageIds = new Set(_mapGroupToMessageIds(previousGroup));
    return  group.messages.some(message => previousMessageIds.has(message.messageId));
}
const _mapGroupToMessageIds = (group: ChatMessageGroup): (string|undefined)[] =>
    group.messages.map(message => message.messageId);

const _mapGroupsToPreviousIds = (groups: ChatMessageGroup[], previousGroups: ChatMessageGroup[]): ChatMessageGroup[] =>
    groups.map(group => {
        const previousGroup = _findPreviousGroup(previousGroups, group);
        return previousGroup ? with_(group, {id: previousGroup.id}) : group;
    })

const _groupMessages = (messages: ChatMessage[]): ChatMessageGroup[] =>
    messages.reduce((groups: ChatMessageGroup[], message: ChatMessage) => {
        const lastGroup = groups[groups.length - 1];

        if (lastGroup && lastGroup.senderId === message.senderId) {
            lastGroup.messages.push(message);
        } else {
            const newGroup: ChatMessageGroup = {
                id: message.senderId + ( message.internalReferenceId ?? message.messageId ?? uuidv4()),
                senderId: message.senderId,
                senderName: message.senderName,
                messages: [message],
                ownUser: message.ownMessage
            };
            groups.push(newGroup);
        }

        return groups;
    }, []);

